import React, { useContext, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Cookies from "js-cookie";
import Icon from "@mdi/react";
import {
    mdiMenu,
    mdiAccountSchool,
    mdiAccountGroup,
    mdiBookOpenPageVariant,
    mdiCurrencyRupee,
    mdiBriefcaseCheck,
    mdiPlusCircleOutline,
    mdiFormatListChecks,
    mdiClipboardTextOutline,
    mdiCashMultiple,
    mdiAccountPlus,
    mdiTrendingUp,
    mdiCalendarClock,
    mdiMagnify,
    mdiBellOutline,
    mdiChevronRight,
    mdiCheckCircleOutline,
    mdiClockOutline,
    mdiDotsVertical,
    mdiSchool,
    mdiFileDocumentOutline,
    mdiAlertCircleOutline,
    mdiLogout
} from "@mdi/js";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import axios from "axios";
import logo from "../assets/frontimg/logo.jpg";
import bannerBg from "../assets/images/dashboard-banner.jpg";
import InnerHeader from "./InnerHeader";
import { SidebarContext } from "../context/SideBarContext";
import { BASE_URL } from "./BaseUrl";

// Register Chart.js elements
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const AdminDashBoard = () => {
    const { isSidebarOpen } = useContext(SidebarContext);
    const navigate = useNavigate();

    const [currentDate, setCurrentDate] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [stats, setStats] = useState({
        activeStudents: 1248,
        monthlyInquiries: 342,
        convertedAdmissions: 84,
        activeBatches: 28,
        feeCollection: "18.45L",
        placementDrives: 15
    });

    useEffect(() => {
        const today = new Date();
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        setCurrentDate(today.toLocaleDateString('en-US', options));
        fetchDashboardMetrics();
    }, []);

    const fetchDashboardMetrics = async () => {
        try {
            const courseRes = await axios.get(`${BASE_URL}/getCourse`);
            const todayStr = new Date().toISOString().split("T")[0];
            const batchRes = await axios.post(`${BASE_URL}/getUpcomingBatch`, { date: todayStr });

            setStats(prev => ({
                ...prev,
                activeBatches: Array.isArray(batchRes.data) && batchRes.data.length > 0 ? batchRes.data.length : prev.activeBatches,
            }));
        } catch (err) {
            console.log("Stats fetch fallback to default metrics:", err);
        }
    };

    const handlelogout = () => {
        const confirm = window.confirm("Do you want to logout from Apachi Institute Console?");
        if (confirm) {
            Cookies.remove("token");
            Cookies.remove("role");
            Cookies.remove("userid");
            localStorage.clear();
            sessionStorage.clear();
            navigate(`/weblog`);
        }
    };

    // Admissions & Inquiry Line Chart Configuration
    const admissionsChartData = {
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug"],
        datasets: [
            {
                label: "Admissions Confirmed",
                data: [65, 82, 95, 110, 140, 125, 160, 185],
                borderColor: "#4f46e5",
                backgroundColor: "rgba(79, 70, 229, 0.12)",
                fill: true,
                tension: 0.4,
                pointBackgroundColor: "#4f46e5",
                pointRadius: 4,
            },
            {
                label: "Inquiries Received",
                data: [120, 150, 180, 210, 260, 230, 290, 340],
                borderColor: "#06b6d4",
                backgroundColor: "transparent",
                borderDash: [5, 5],
                tension: 0.4,
                pointRadius: 0,
            }
        ]
    };

    const admissionsChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: { usePointStyle: true, boxWidth: 8, font: { family: "'Inter', sans-serif", size: 12 } }
            },
            tooltip: {
                padding: 12,
                backgroundColor: '#1e293b',
                cornerRadius: 8
            }
        },
        scales: {
            x: { grid: { display: false } },
            y: { grid: { color: '#f1f5f9' }, ticks: { stepSize: 50 } }
        }
    };

    // Lead Sources Doughnut Chart Configuration
    const sourcesData = {
        labels: ["Walk-in", "Digital Ads", "Website Portal", "Student Referral", "Campus Drive"],
        datasets: [
            {
                data: [38, 26, 18, 12, 6],
                backgroundColor: ["#4f46e5", "#06b6d4", "#10b981", "#f59e0b", "#8b5cf6"],
                borderWidth: 0,
                hoverOffset: 6
            }
        ]
    };

    const sourcesOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "75%",
        plugins: {
            legend: {
                position: 'bottom',
                labels: { usePointStyle: true, boxWidth: 8, padding: 12, font: { size: 11 } }
            }
        }
    };

    // Active Batch Progress Data
    const activeBatchesList = [
        { name: "Full Stack Web Dev (#FS-402)", progress: 85, students: "32/35 Students", date: "Ends Sep 15", color: "bg-primary" },
        { name: "Python Data Science (#DS-108)", progress: 62, students: "28/30 Students", date: "Ends Oct 10", color: "bg-success" },
        { name: "Cyber Security Pro (#CS-204)", progress: 45, students: "25/25 Students", date: "Ends Nov 05", color: "bg-info" },
        { name: "Cloud & DevOps (#DO-301)", progress: 25, students: "30/30 Students", date: "Ends Dec 01", color: "bg-warning" },
    ];

    // Upcoming Fee Due Alerts Data
    const feeAlerts = [
        { student: "Amit Verma", batch: "Batch #FS-402", amount: "₹15,000", dueDate: "Today", status: "Overdue" },
        { student: "Priya Nair", batch: "Batch #DS-108", amount: "₹20,000", dueDate: "Tomorrow", status: "Pending" },
        { student: "Siddharth Joshi", batch: "Batch #CS-204", amount: "₹18,500", dueDate: "23 Aug", status: "Scheduled" },
    ];

    // Live Activity Stream
    const recentActivities = [
        { title: "New Admission Confirmed", desc: "Neha Gupta enrolled in Data Science (Batch #DS-108)", time: "8 mins ago", icon: mdiCheckCircleOutline, color: "text-success", bg: "bg-success-subtle" },
        { title: "Fee Receipt Issued", desc: "₹25,000 payment received from ID: STU-8941", time: "22 mins ago", icon: mdiCurrencyRupee, color: "text-primary", bg: "bg-primary-subtle" },
        { title: "TCS Placement Drive", desc: "40 candidate CVs submitted for shortlisting", time: "1 hour ago", icon: mdiBriefcaseCheck, color: "text-purple", bg: "bg-purple-subtle" },
        { title: "Lecture Syllabus Update", desc: "Lecture 14 completed for Fullstack Batch #FS-402", time: "2 hours ago", icon: mdiFileDocumentOutline, color: "text-info", bg: "bg-info-subtle" },
    ];

    return (
        <div className={`container-fluid page-body-wrapper ${isSidebarOpen ? "col-lg-12" : ""}`} style={{ backgroundColor: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', system-ui, sans-serif" }}>
            <InnerHeader />
            
            {/* Main Content Header Bar */}


            <div className="main-panel">
                <div className="content-wrapper p-3 p-md-4">
                    
                    {/* Welcome Hero Banner */}
                    <div className="card border-0 rounded-4 mb-4 text-white shadow-lg overflow-hidden position-relative" 
                         style={{ 
                             backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.92) 0%, rgba(30, 27, 75, 0.88) 55%, rgba(49, 46, 129, 0.75) 100%), url(${bannerBg})`,
                             backgroundSize: "cover",
                             backgroundPosition: "center right",
                             backgroundRepeat: "no-repeat",
                             minHeight: "160px"
                         }}>
                        <div className="position-absolute top-0 end-0 p-5 opacity-10 pointer-events-none" style={{ right: "-20px", top: "-20px" }}>
                            <Icon path={mdiSchool} size={8} />
                        </div>

                        <div className="card-body p-4 position-relative" style={{ zIndex: 1 }}>
                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                                <div>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <span className="badge rounded-pill text-white" style={{ fontSize: "0.75rem", backgroundColor: "rgba(99, 102, 241, 0.35)", border: "1px solid rgba(165, 180, 252, 0.4)" }}>
                                            Apachi Institute Console • Administration
                                        </span>
                                        <span className="text-white-50 small">•</span>
                                        <span className="text-white-50 small">{currentDate}</span>
                                    </div>
                                    <h2 className="fw-bold mb-1 text-white" style={{ letterSpacing: "-0.5px" }}>
                                        Welcome to Apachi Institute Console 👋
                                    </h2>
                                    <p className="text-white-50 mb-0 small">
                                        Real-time overview of student admissions, inquiries, batches, and operations.
                                    </p>
                                </div>

                                <div className="d-flex align-items-center flex-wrap" style={{ gap: "16px" }}>
                                    <Link 
                                        to="/manualadmission" 
                                        className="btn btn-primary d-inline-flex align-items-center gap-2 px-3 py-2 rounded-3 shadow-sm fw-semibold text-decoration-none"
                                        style={{ backgroundColor: "#4f46e5", borderColor: "#4f46e5", marginRight: "16px" }}
                                    >
                                        <Icon path={mdiAccountPlus} size={0.8} />
                                        <span>New Admission</span>
                                    </Link>
                                    <button 
                                        type="button" 
                                        onClick={handlelogout} 
                                        className="btn btn-danger d-inline-flex align-items-center gap-2 px-3 py-2 rounded-3 shadow-sm fw-semibold"
                                        style={{ 
                                            backgroundColor: "#ef4444", 
                                            borderColor: "#ef4444"
                                        }}
                                        title="Logout from Apachi Institute Console"
                                    >
                                        <Icon path={mdiLogout} size={0.8} />
                                        <span>Logout</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 1: Executive KPI Summary Bar */}
                    <div className="row g-3 mb-4">
                        {/* Active Students */}
                        <div className="col-12 col-sm-6 col-xl-3">
                            <Link to="/admissionlisting" className="text-decoration-none">
                                <div className="card border-0 rounded-4 shadow-sm h-100 bg-white p-3 hover-lift transition" style={{ borderLeft: "4px solid #4f46e5" }}>
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span className="text-uppercase text-secondary fw-bold" style={{ fontSize: "0.72rem", letterSpacing: "0.5px" }}>Active Students</span>
                                        <div className="p-2 rounded-3 text-primary" style={{ backgroundColor: "#e0e7ff" }}>
                                            <Icon path={mdiAccountSchool} size={1.1} />
                                        </div>
                                    </div>
                                    <h3 className="fw-bold text-dark mb-1">{stats.activeStudents}</h3>
                                    <small className="text-success fw-semibold" style={{ fontSize: "0.75rem" }}>
                                        <Icon path={mdiTrendingUp} size={0.5} /> +12.4% vs last month →
                                    </small>
                                </div>
                            </Link>
                        </div>

                        {/* Inquiries */}
                        <div className="col-12 col-sm-6 col-xl-3">
                            <Link to="/inquirylisting" className="text-decoration-none">
                                <div className="card border-0 rounded-4 shadow-sm h-100 bg-white p-3 hover-lift transition" style={{ borderLeft: "4px solid #06b6d4" }}>
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span className="text-uppercase text-secondary fw-bold" style={{ fontSize: "0.72rem", letterSpacing: "0.5px" }}>Monthly Inquiries</span>
                                        <div className="p-2 rounded-3 text-info" style={{ backgroundColor: "#cff4fc" }}>
                                            <Icon path={mdiClipboardTextOutline} size={1.1} />
                                        </div>
                                    </div>
                                    <h3 className="fw-bold text-dark mb-1">{stats.monthlyInquiries}</h3>
                                    <small className="text-info fw-semibold" style={{ fontSize: "0.75rem" }}>
                                        {stats.convertedAdmissions} Converted (24.5% conversion) →
                                    </small>
                                </div>
                            </Link>
                        </div>

                        {/* Batches */}
                        <div className="col-12 col-sm-6 col-xl-3">
                            <Link to="/batchlisting" className="text-decoration-none">
                                <div className="card border-0 rounded-4 shadow-sm h-100 bg-white p-3 hover-lift transition" style={{ borderLeft: "4px solid #10b981" }}>
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span className="text-uppercase text-secondary fw-bold" style={{ fontSize: "0.72rem", letterSpacing: "0.5px" }}>Active Batches</span>
                                        <div className="p-2 rounded-3 text-success" style={{ backgroundColor: "#d1fae5" }}>
                                            <Icon path={mdiAccountGroup} size={1.1} />
                                        </div>
                                    </div>
                                    <h3 className="fw-bold text-dark mb-1">{stats.activeBatches} Running</h3>
                                    <small className="text-success fw-semibold" style={{ fontSize: "0.75rem" }}>
                                        4 Batches Starting Shortly →
                                    </small>
                                </div>
                            </Link>
                        </div>

                        {/* Fee Collection */}
                        <div className="col-12 col-sm-6 col-xl-3">
                            <Link to="/addfeesdetails" className="text-decoration-none">
                                <div className="card border-0 rounded-4 shadow-sm h-100 bg-white p-3 hover-lift transition" style={{ borderLeft: "4px solid #f59e0b" }}>
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span className="text-uppercase text-secondary fw-bold" style={{ fontSize: "0.72rem", letterSpacing: "0.5px" }}>Fee Collection</span>
                                        <div className="p-2 rounded-3 text-warning" style={{ backgroundColor: "#fef3c7" }}>
                                            <Icon path={mdiCurrencyRupee} size={1.1} />
                                        </div>
                                    </div>
                                    <h3 className="fw-bold text-dark mb-1">₹{stats.feeCollection}</h3>
                                    <small className="text-warning fw-semibold" style={{ fontSize: "0.75rem" }}>
                                        88% Monthly Target Achieved →
                                    </small>
                                </div>
                            </Link>
                        </div>
                    </div>

                    {/* Section 2: Operations Action Launchpad (Quick Shortcuts) */}
                    <div className="card border-0 shadow-sm rounded-4 mb-4 bg-white">
                        <div className="card-body p-4">
                            <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                                <div>
                                    <h6 className="fw-bold text-dark m-0 fs-6">Operations Action Launchpad</h6>
                                    <small className="text-muted">1-Click shortcuts to daily institute activities</small>
                                </div>
                                <span className="badge bg-light text-secondary border px-3 py-1 rounded-pill">Action Hub</span>
                            </div>

                            <div className="row g-3 text-center">
                                <div className="col-6 col-md-4 col-lg-2">
                                    <Link to="/inquirylisting" className="text-decoration-none">
                                        <div className="p-3 rounded-3 bg-light border hover-card transition">
                                            <div className="p-2 rounded-circle bg-primary bg-opacity-10 text-primary mx-auto mb-2" style={{ width: "44px", height: "44px" }}>
                                                <Icon path={mdiClipboardTextOutline} size={1} />
                                            </div>
                                            <div className="fw-bold text-dark small mb-1">Inquiry List</div>
                                            <span className="text-muted" style={{ fontSize: "0.72rem" }}>Lead Records</span>
                                        </div>
                                    </Link>
                                </div>

                                <div className="col-6 col-md-4 col-lg-2">
                                    <Link to="/admissionlisting" className="text-decoration-none">
                                        <div className="p-3 rounded-3 bg-light border hover-card transition">
                                            <div className="p-2 rounded-circle bg-success bg-opacity-10 text-success mx-auto mb-2" style={{ width: "44px", height: "44px" }}>
                                                <Icon path={mdiAccountPlus} size={1} />
                                            </div>
                                            <div className="fw-bold text-dark small mb-1">Admissions</div>
                                            <span className="text-muted" style={{ fontSize: "0.72rem" }}>Student Register</span>
                                        </div>
                                    </Link>
                                </div>

                                <div className="col-6 col-md-4 col-lg-2">
                                    <Link to="/addfeesdetails" className="text-decoration-none">
                                        <div className="p-3 rounded-3 bg-light border hover-card transition">
                                            <div className="p-2 rounded-circle bg-warning bg-opacity-10 text-warning mx-auto mb-2" style={{ width: "44px", height: "44px" }}>
                                                <Icon path={mdiCashMultiple} size={1} />
                                            </div>
                                            <div className="fw-bold text-dark small mb-1">Fee Receipts</div>
                                            <span className="text-muted" style={{ fontSize: "0.72rem" }}>Record Payment</span>
                                        </div>
                                    </Link>
                                </div>

                                <div className="col-6 col-md-4 col-lg-2">
                                    <Link to="/lecturetaken" className="text-decoration-none">
                                        <div className="p-3 rounded-3 bg-light border hover-card transition">
                                            <div className="p-2 rounded-circle bg-info bg-opacity-10 text-info mx-auto mb-2" style={{ width: "44px", height: "44px" }}>
                                                <Icon path={mdiFormatListChecks} size={1} />
                                            </div>
                                            <div className="fw-bold text-dark small mb-1">Lectures Log</div>
                                            <span className="text-muted" style={{ fontSize: "0.72rem" }}>Syllabus Taken</span>
                                        </div>
                                    </Link>
                                </div>

                                <div className="col-6 col-md-4 col-lg-2">
                                    <Link to="/cvshortlisted" className="text-decoration-none">
                                        <div className="p-3 rounded-3 bg-light border hover-card transition">
                                            <div className="p-2 rounded-circle bg-purple bg-opacity-10 mx-auto mb-2" style={{ width: "44px", height: "44px", color: "#8b5cf6" }}>
                                                <Icon path={mdiBriefcaseCheck} size={1} />
                                            </div>
                                            <div className="fw-bold text-dark small mb-1">CV Shortlists</div>
                                            <span className="text-muted" style={{ fontSize: "0.72rem" }}>Placements</span>
                                        </div>
                                    </Link>
                                </div>

                                <div className="col-6 col-md-4 col-lg-2">
                                    <Link to="/bookissue" className="text-decoration-none">
                                        <div className="p-3 rounded-3 bg-light border hover-card transition">
                                            <div className="p-2 rounded-circle bg-secondary bg-opacity-10 text-secondary mx-auto mb-2" style={{ width: "44px", height: "44px" }}>
                                                <Icon path={mdiBookOpenPageVariant} size={1} />
                                            </div>
                                            <div className="fw-bold text-dark small mb-1">Library Desk</div>
                                            <span className="text-muted" style={{ fontSize: "0.72rem" }}>Book Circulation</span>
                                        </div>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 3: Interactive Visual Charts */}
                    <div className="row g-3 mb-4">
                        {/* Line Chart: Admissions vs Inquiries */}
                        <div className="col-12 col-xl-8">
                            <div className="card border-0 rounded-4 shadow-sm h-100 bg-white">
                                <div className="card-body p-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div>
                                            <h6 className="fw-bold text-dark m-0 fs-6">Admissions & Inquiry Conversion Growth</h6>
                                            <small className="text-muted">Month-over-month candidate inquiry vs admission conversion</small>
                                        </div>
                                        <span className="badge bg-light text-secondary border px-3 py-1 rounded-pill">Visual Analytics</span>
                                    </div>
                                    <div style={{ height: "290px" }}>
                                        <Line data={admissionsChartData} options={admissionsChartOptions} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Donut Chart: Lead Acquisition Sources */}
                        <div className="col-12 col-xl-4">
                            <div className="card border-0 rounded-4 shadow-sm h-100 bg-white">
                                <div className="card-body p-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="fw-bold text-dark m-0 fs-6">Inquiry Channel Share</h6>
                                        <button className="btn btn-link text-muted p-0 me-0">
                                            <Icon path={mdiDotsVertical} size={0.8} />
                                        </button>
                                    </div>
                                    <div style={{ height: "230px" }} className="position-relative">
                                        <Doughnut data={sourcesData} options={sourcesOptions} />
                                    </div>
                                    <div className="text-center mt-3 pt-2 border-top">
                                        <small className="text-muted">Total Active Inquiries Tracked: <strong className="text-dark">1,480</strong></small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 4: Live Operational Widgets */}
                    <div className="row g-3">
                        {/* Active Batch Progress Tracker */}
                        <div className="col-12 col-lg-6">
                            <div className="card border-0 rounded-4 shadow-sm h-100 bg-white">
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                                        <div>
                                            <h6 className="fw-bold text-dark m-0 fs-6">Active Training Batches Syllabus Progress</h6>
                                            <small className="text-muted">Current batch completion percentages</small>
                                        </div>
                                        <Link to="/batchlisting" className="text-primary text-decoration-none small fw-semibold">
                                            View All Batches <Icon path={mdiChevronRight} size={0.6} />
                                        </Link>
                                    </div>

                                    <div className="d-flex flex-column gap-3 mt-3">
                                        {activeBatchesList.map((batch, index) => (
                                            <div key={index} className="p-3 rounded-3 bg-light border-0">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <span className="fw-bold text-dark small">{batch.name}</span>
                                                    <span className="badge bg-white text-secondary border small">{batch.students}</span>
                                                </div>
                                                <div className="progress rounded-pill mb-2" style={{ height: "8px" }}>
                                                    <div 
                                                        className={`progress-bar ${batch.color} rounded-pill`} 
                                                        role="progressbar" 
                                                        style={{ width: `${batch.progress}%` }}
                                                    ></div>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center text-muted" style={{ fontSize: "0.75rem" }}>
                                                    <span>Completed: <strong className="text-dark">{batch.progress}%</strong></span>
                                                    <span><Icon path={mdiClockOutline} size={0.5} className="me-1" />{batch.date}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Upcoming Fee Installment Alerts */}
                        <div className="col-12 col-lg-6">
                            <div className="card border-0 rounded-4 shadow-sm h-100 bg-white">
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                                        <div>
                                            <h6 className="fw-bold text-dark m-0 fs-6">Fee Installments Scheduled</h6>
                                            <small className="text-muted">Upcoming fee collection follow-ups</small>
                                        </div>
                                        <Link to="/feesreport" className="text-warning text-decoration-none small fw-semibold">
                                            Fees Report <Icon path={mdiChevronRight} size={0.6} />
                                        </Link>
                                    </div>

                                    <div className="d-flex flex-column gap-3 mt-3">
                                        {feeAlerts.map((fee, idx) => (
                                            <div key={idx} className="d-flex align-items-center justify-content-between p-3 rounded-3 border bg-light">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="p-2 rounded-circle bg-warning bg-opacity-10 text-warning">
                                                        <Icon path={mdiAlertCircleOutline} size={0.9} />
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold text-dark small">{fee.student}</div>
                                                        <small className="text-muted">{fee.batch}</small>
                                                    </div>
                                                </div>
                                                <div className="text-end">
                                                    <div className="fw-bold text-dark">{fee.amount}</div>
                                                    <span className={`badge ${fee.status === 'Overdue' ? 'bg-danger-subtle text-danger' : 'bg-warning-subtle text-warning-emphasis'} rounded-pill small`}>
                                                        {fee.dueDate}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Live System Activity Feed Stream */}
                                    <div className="mt-4 pt-3 border-top">
                                        <h6 className="fw-bold text-dark mb-3 fs-6">Live Activity Log</h6>
                                        <div className="d-flex flex-column gap-2">
                                            {recentActivities.slice(0, 2).map((act, i) => (
                                                <div key={i} className="d-flex align-items-start gap-2 p-2 rounded-3 hover-bg-light transition">
                                                    <div className={`p-2 rounded-circle ${act.bg} ${act.color} flex-shrink-0 mt-1`}>
                                                        <Icon path={act.icon} size={0.7} />
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <span className="fw-bold text-dark small">{act.title}</span>
                                                            <span className="text-muted" style={{ fontSize: "0.7rem" }}>{act.time}</span>
                                                        </div>
                                                        <p className="text-secondary small mb-0 mt-1" style={{ fontSize: "0.78rem" }}>{act.desc}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default AdminDashBoard;
